import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { InnerLayout } from '../components/layout/InnerLayout';
import { ShieldAlert } from 'lucide-react';
import { OrganizationViewProps, OrgTabKey, OrgSettingsTabKey } from './organization/types';
import { useOrganizationData } from './organization/useOrganizationData';
import { useOrganizationHeaderNav } from './organization/components/useOrganizationHeaderNav';
import { OrganizationOverviewTab } from './organization/tabs/OrganizationOverviewTab';
import { OrganizationProjectsTab } from './organization/tabs/OrganizationProjectsTab';
import { OrganizationTeamTab } from './organization/tabs/OrganizationTeamTab';
import { OrganizationGeneralSettingsTab } from './organization/tabs/OrganizationGeneralSettingsTab';
import { OrganizationBillingSection } from './organization/components/OrganizationBillingSection';
import { OrganizationDangerTab } from './organization/tabs/OrganizationDangerTab';
import { OrganizationModals } from './organization/modals/OrganizationModals';

export const OrganizationView: React.FC<OrganizationViewProps> = ({ section: propSection }) => {
  const { orgId: routeOrgId } = useParams<{ orgId?: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { currentOrg, projects } = useAuth();
  const { showToast } = useToast();

  const currentOrgId = currentOrg?.id || routeOrgId || '';

  const activeTab: OrgTabKey = useMemo(() => {
    if (propSection) {
      if (propSection === 'members') return 'team';
      return propSection;
    }
    const path = location.pathname;
    if (path.endsWith('/projects')) return 'projects';
    if (path.endsWith('/members') || path.endsWith('/team')) return 'team';
    if (path.endsWith('/settings')) return 'settings';
    const tabParam = searchParams.get('tab');
    if (tabParam === 'projects') return 'projects';
    if (tabParam === 'team' || tabParam === 'members') return 'team';
    if (tabParam === 'settings' || tabParam === 'plan') return 'settings';
    return 'overview';
  }, [propSection, location.pathname, searchParams]);

  const [settingsTab, setSettingsTab] = useState<OrgSettingsTabKey>(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'plan' || tabParam === 'danger') return tabParam;
    const subtabParam = searchParams.get('subtab');
    if (subtabParam === 'plan' || subtabParam === 'danger') return subtabParam;
    return 'general';
  });

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'plan' || tabParam === 'danger' || tabParam === 'general') {
      setSettingsTab(tabParam);
    }
    const subtabParam = searchParams.get('subtab');
    if (subtabParam === 'plan' || subtabParam === 'danger' || subtabParam === 'general') {
      setSettingsTab(subtabParam);
    }
  }, [searchParams]);

  const data = useOrganizationData(activeTab, settingsTab);

  const { headerTitle, headerDescription, headerActions, navItems } = useOrganizationHeaderNav({
    activeTab,
    settingsTab,
    setSettingsTab,
    currentOrg,
    currentOrgId,
    projects,
    data,
  });

  return (
    <InnerLayout
      header={{
        title: headerTitle,
        description: headerDescription,
        actions: headerActions,
      }}
      navItems={navItems}
    >
      {activeTab === 'overview' && (
        <OrganizationOverviewTab
          quota={data.quota}
          currentOrg={data.currentOrg}
          projects={data.projects}
          membersCount={data.members.length}
          canManageOrg={data.canManageOrg}
          onCreateProjectClick={() => data.setCreateProjectOpen(true)}
        />
      )}

      {activeTab === 'projects' && (
        <OrganizationProjectsTab
          projects={data.projects}
          currentOrg={data.currentOrg}
          canManageOrg={data.canManageOrg}
          onCreateProjectClick={() => data.setCreateProjectOpen(true)}
        />
      )}

      {activeTab === 'team' && (
        <OrganizationTeamTab
          members={data.members}
          isLoadingMembers={data.isLoadingMembers}
          canManageOrg={data.canManageOrg}
          user={data.user}
          invitations={data.invitations}
          onManageProjects={data.openManageProjects}
          onSelectMemberToRemove={data.setMemberToRemove}
          onRevokeInvitation={data.handleRevokeInvitation}
          isRevokingInvite={data.isRevokingInvite}
          showToast={showToast}
        />
      )}

      {activeTab === 'settings' && !data.canManageOrg && (
        <div className="p-8 rounded-lg bg-cf-surface border border-cf-border text-center max-w-md mx-auto my-12 space-y-3">
          <ShieldAlert size={36} className="text-amber-500 mx-auto" />
          <h3 className="text-base font-bold text-cf-textPrimary">Access Restricted</h3>
          <p className="text-xs text-cf-textSecondary">
            Only organization owners and administrators can configure organization settings, quotas, and billing.
          </p>
        </div>
      )}

      {activeTab === 'settings' && data.canManageOrg && (
        <div className="space-y-6">
          {settingsTab === 'general' && (
            <OrganizationGeneralSettingsTab
              currentOrg={data.currentOrg}
              orgName={data.orgName}
              onOrgNameChange={data.setOrgName}
              canManageOrg={data.canManageOrg}
              isSavingOrg={data.isSavingOrg}
              onSaveOrg={data.handleSaveOrgSettings}
              copiedOrgId={data.copiedOrgId}
              onCopyOrgId={data.copyOrgId}
            />
          )}

          {settingsTab === 'plan' && (
            <OrganizationBillingSection
              currentOrg={data.currentOrg}
              quota={data.quota}
              isLoadingQuota={data.isLoadingQuota}
              plans={data.plans}
              isLoadingPlans={data.isLoadingPlans}
              canManageOrg={data.canManageOrg}
              isMutatingSubscription={data.isMutatingSubscription}
              onSelectUpgrade={data.setUpgradeTargetPlan}
              onSelectDowngrade={data.setDowngradeTargetPlan}
              onOpenCancelModal={() => data.setCancelModalOpen(true)}
              onResumeSubscription={data.handleResumeSubscription}
              membersCount={data.members.length}
            />
          )}

          {settingsTab === 'danger' && (
            <OrganizationDangerTab
              currentOrg={data.currentOrg}
              canDeleteOrg={data.canDeleteOrg}
              onOpenDeleteModal={() => {
                data.setDeleteOrgConfirmText('');
                data.setDeleteOrgModalOpen(true);
              }}
            />
          )}
        </div>
      )}

      <OrganizationModals data={data} />
    </InnerLayout>
  );
};
